import { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Avatar,
  IconButton,
  Stack,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
} from "../../../../pages/Home/constants/homeGlass";
import { getSelectableClientRoles } from "../../../../core/utils/util";
import {
  DARK_BORDER,
  THEME_ACCENT,
  glassPanelSx,
  userTableColSx,
  rolesTableColSx,
  roleSelectWrapperSx,
  roleSelectSx,
  roleChipsRowSx,
  roleChipSx,
  selectMenuPaperSx,
  userAutocompleteSx,
  userPickerTextFieldSx,
  userPickerValueSx,
  userPickerNamesRowSx,
  getUserLabel,
  getUserHandle,
  isCreatorUser,
  StepSectionTitle,
  FieldLabel,
} from "./shared";

function UserPickerOption({ user, selected }) {
  const label = getUserLabel(user);
  const handle = getUserHandle(user);

  return (
    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ width: "100%" }}>
      <Checkbox
        checked={selected}
        sx={{
          color: "rgba(255,255,255,0.65)",
          p: 0,
          "&.Mui-checked": { color: THEME_ACCENT },
        }}
      />
      <Avatar
        src={user.profileImageUrl || undefined}
        sx={{
          width: 32,
          height: 32,
          fontSize: 13,
          bgcolor: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.14)",
        }}
      >
        {label.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }} noWrap>
          {label}
        </Typography>
        {handle ? (
          <Typography
            sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.45)", lineHeight: 1.3, mt: 0.15 }}
            noWrap
          >
            {handle}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

function SelectedUsersValueDisplay({ users }) {
  if (!users.length) return null;

  return (
    <Box sx={userPickerValueSx}>
      <Stack direction="row" spacing={-0.6} sx={{ flexShrink: 0 }}>
        {users.slice(0, 3).map((user) => (
          <Avatar
            key={user.uid}
            src={user.profileImageUrl || undefined}
            sx={{
              width: 24,
              height: 24,
              fontSize: 11,
              bgcolor: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.35)",
            }}
          >
            {getUserLabel(user).charAt(0).toUpperCase()}
          </Avatar>
        ))}
      </Stack>
      <Box sx={userPickerNamesRowSx}>{users.map(getUserLabel).join(", ")}</Box>
    </Box>
  );
}

export default function ClientUsersStep({
  allUsers,
  usersLoading,
  clientRoles,
  clientRolesLoading,
  userAssignments,
  creatorUser,
  isAdmin = false,
  onSelectedUsersChange,
  onRemoveUser,
  onUpdateRoles,
}) {
  const [pickerInput, setPickerInput] = useState("");

  const userById = useMemo(() => {
    const map = new Map();
    allUsers.forEach((user) => map.set(String(user.uid), user));
    return map;
  }, [allUsers]);

  const pickerUsers = useMemo(
    () => allUsers.filter((user) => !isCreatorUser(user, creatorUser)),
    [allUsers, creatorUser],
  );

  const visibleAssignments = useMemo(
    () =>
      userAssignments.filter((entry) => {
        const user = userById.get(String(entry.userId));
        return user && !isCreatorUser(user, creatorUser);
      }),
    [userAssignments, userById, creatorUser],
  );

  const roleById = useMemo(() => {
    const map = new Map();
    getSelectableClientRoles(clientRoles, isAdmin).forEach((role) => {
      const id = Number(role.roleId);
      if (Number.isFinite(id)) {
        map.set(id, role);
      }
    });
    return map;
  }, [clientRoles, isAdmin]);

  const selectableClientRoles = useMemo(
    () => getSelectableClientRoles(clientRoles, isAdmin),
    [clientRoles, isAdmin],
  );

  const selectedUsers = useMemo(
    () =>
      visibleAssignments
        .map((entry) => userById.get(String(entry.userId)))
        .filter(Boolean),
    [visibleAssignments, userById],
  );

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <StepSectionTitle icon={GroupsOutlinedIcon}>Assign Users &amp; Roles</StepSectionTitle>
      <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.45)", mb: 1.5, lineHeight: 1.5 }}>
        Select users and assign client roles for this client.
      </Typography>

      <FieldLabel>Users</FieldLabel>
      <Autocomplete
        multiple
        openOnFocus
        disableCloseOnSelect
        options={pickerUsers}
        loading={usersLoading}
        value={selectedUsers}
        inputValue={pickerInput}
        onInputChange={(_, value) => setPickerInput(value)}
        onChange={(_, users) => onSelectedUsersChange(users)}
        getOptionLabel={getUserLabel}
        isOptionEqualToValue={(option, value) =>
          String(option.uid) === String(value.uid)
        }
        filterSelectedOptions={false}
        noOptionsText={usersLoading ? "Loading users..." : "No users found"}
        renderTags={() => null}
        renderOption={(props, user, { selected }) => (
          <Box
            component="li"
            {...props}
            key={user.uid}
            sx={{
              px: 1.25,
              py: 0.85,
              "&[aria-selected='true']": { bgcolor: "rgba(255,255,255,0.04) !important" },
              "&.Mui-focused": { bgcolor: "rgba(255,255,255,0.06) !important" },
            }}
          >
            <UserPickerOption user={user} selected={selected} />
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            placeholder={selectedUsers.length ? "" : "Search and select users"}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <SelectedUsersValueDisplay users={selectedUsers} />
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {usersLoading ? (
                    <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={userPickerTextFieldSx}
          />
        )}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              bgcolor: "rgba(28, 29, 22, 0.96)",
              backdropFilter: HOME_GLASS_BACKDROP,
              WebkitBackdropFilter: HOME_GLASS_BACKDROP,
              border: HOME_GLASS_BORDER,
              color: "#fff",
              borderRadius: "12px",
              boxShadow: "0 12px 32px rgba(0,0,0,0.28)",
              "& .MuiAutocomplete-listbox": {
                py: 0.5,
                maxHeight: 280,
              },
            },
          },
        }}
        sx={{ mb: 2, ...userAutocompleteSx }}
      />

      <Box
        sx={{
          ...glassPanelSx,
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          <Table
            size="small"
            stickyHeader
            sx={{ tableLayout: "fixed", width: "100%" }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...userTableColSx,
                    fontWeight: 700,
                    fontSize: 12,
                    color: "rgba(255,255,255,0.55)",
                    bgcolor: "rgba(255,255,255,0.04)",
                    borderBottom: `1px solid ${DARK_BORDER}`,
                  }}
                >
                  Users
                </TableCell>
                <TableCell
                  sx={{
                    ...rolesTableColSx,
                    fontWeight: 700,
                    fontSize: 12,
                    color: "rgba(255,255,255,0.55)",
                    bgcolor: "rgba(255,255,255,0.04)",
                    borderBottom: `1px solid ${DARK_BORDER}`,
                  }}
                >
                  Client Roles
                </TableCell>
                <TableCell
                  sx={{
                    width: 44,
                    bgcolor: "rgba(255,255,255,0.04)",
                    borderBottom: `1px solid ${DARK_BORDER}`,
                  }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleAssignments.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{
                      py: 3,
                      textAlign: "center",
                      color: "rgba(255,255,255,0.4)",
                      fontSize: 13,
                      borderBottom: "none",
                    }}
                  >
                    No users selected yet. Use the user picker above to select users.
                  </TableCell>
                </TableRow>
              ) : (
                visibleAssignments.map((entry) => {
                  const user = userById.get(String(entry.userId));
                  if (!user) return null;

                  return (
                    <TableRow key={entry.userId}>
                      <TableCell
                        sx={{
                          ...userTableColSx,
                          color: "#f8fafc",
                          fontSize: 13.5,
                          fontWeight: 600,
                          borderBottom: `1px solid ${DARK_BORDER}`,
                          verticalAlign: "middle",
                        }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="center">
                          <Avatar
                            src={user.profileImageUrl || undefined}
                            sx={{
                              width: 30,
                              height: 30,
                              fontSize: 12,
                              bgcolor: "rgba(255,255,255,0.12)",
                              border: "1px solid rgba(255,255,255,0.14)",
                            }}
                          >
                            {getUserLabel(user).charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>
                              {getUserLabel(user)}
                            </Typography>
                            {getUserHandle(user) ? (
                              <Typography
                                sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.4)", mt: 0.2 }}
                                noWrap
                              >
                                {getUserHandle(user)}
                              </Typography>
                            ) : null}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell
                        sx={{
                          ...rolesTableColSx,
                          borderBottom: `1px solid ${DARK_BORDER}`,
                          verticalAlign: "middle",
                        }}
                      >
                        <Box sx={roleSelectWrapperSx}>
                        <Select
                          multiple
                          fullWidth
                          size="small"
                          displayEmpty
                          disabled={clientRolesLoading}
                          value={entry.roles}
                          onChange={(event) => {
                            const value = event.target.value;
                            onUpdateRoles(
                              entry.userId,
                              typeof value === "string" ? value.split(",") : value,
                            );
                          }}
                          renderValue={(selected) => {
                            if (clientRolesLoading) {
                              return (
                                <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.35)" }}>
                                  Loading roles...
                                </Typography>
                              );
                            }
                            if (!selected.length) {
                              return (
                                <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.35)" }}>
                                  Select roles
                                </Typography>
                              );
                            }
                            return (
                              <Box sx={roleChipsRowSx}>
                                {selected.map((roleId) => {
                                  const role = roleById.get(Number(roleId));
                                  return (
                                  <Chip
                                    key={roleId}
                                    label={role?.roleName ?? roleId}
                                    size="small"
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onDelete={(event) => {
                                      event.stopPropagation();
                                      onUpdateRoles(
                                        entry.userId,
                                        entry.roles.filter(
                                          (id) => String(id) !== String(roleId),
                                        ),
                                      );
                                    }}
                                    deleteIcon={<CloseIcon />}
                                    sx={roleChipSx}
                                  />
                                  );
                                })}
                              </Box>
                            );
                          }}
                          sx={roleSelectSx}
                          MenuProps={{ PaperProps: { sx: selectMenuPaperSx } }}
                        >
                          {selectableClientRoles.map((role) => (
                            <MenuItem
                              key={role.roleId ?? role.roleName}
                              value={Number(role.roleId)}
                            >
                              {role.roleName}
                            </MenuItem>
                          ))}
                        </Select>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: `1px solid ${DARK_BORDER}`,
                          verticalAlign: "middle",
                        }}
                      >
                        <IconButton
                          size="small"
                          aria-label={`Remove ${getUserLabel(user)}`}
                          onClick={() => onRemoveUser(entry.userId)}
                          sx={{
                            color: "rgba(255,255,255,0.45)",
                            "&:hover": { color: "#ff8a80", background: "rgba(255,255,255,0.06)" },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
}
